import styled from "styled-components";
import { Pointer } from "./svg";
import {
	StyledTooltipProps,
	StyledTooltipPointerProps,
} from "../Tooltip.types";

const TRANSITION_DURATION = 200;
const TRANSITION_TIMING_FUNCTION = "ease-in-out";

const TOOLTIP_POINTER_HEIGHT = 8;
const TOOLTIP_POINTER_WIDTH = 24;
const TOOLTIP_POINTER_SPACE = 4;

const StyledTooltip = styled.div<StyledTooltipProps>`
	position: fixed;
	max-width: 224px;
	box-sizing: border-box;
	pointer-events: none;
	opacity: ${({ showTooltip }) => (showTooltip ? 1 : 0)};
	transition: opacity ${TRANSITION_DURATION}ms ${({ delay }) => delay}ms
		${TRANSITION_TIMING_FUNCTION};

	${({
		theme,
		colorOn,
		placement,
		alignment,
		triggerCoordinates,
		tooltipDimensions,
	}) => {
		const COLOR_BG_DARK_VARIANT = theme.palette.hexForRole("bg_dark_variant");
		const COLOR_BG_WHITE = theme.palette.hexForRole("bg_white");
		const COLOR_FG_LIGHT_3 = theme.palette.hexForRole("fg_light_3");
		const COLOR_FG_DARK_1 = theme.palette.hexForRole("fg_dark_1");

		const { width, height } = tooltipDimensions;
		const {
			top,
			left,
			height: triggerHeight,
			width: triggerWidth,
		} = triggerCoordinates;

		const totalPointerSpace = TOOLTIP_POINTER_HEIGHT + TOOLTIP_POINTER_SPACE;
		const totalHeight = height + totalPointerSpace;
		const totalWidth = width + totalPointerSpace;

		const horizontalAlignTransform = {
			start: 0,
			center: `-${(width - triggerWidth) / 2}px`,
			end: `-${width - triggerWidth}px`,
		}[alignment];

		const verticalAlignTransform = {
			start: 0,
			center: `-${(height - triggerHeight) / 2}px`,
			end: `-${height - triggerHeight}px`,
		}[alignment];

		const transform = {
			top: `translate(${horizontalAlignTransform}, -${totalHeight}px)`,
			bottom: `translate(${horizontalAlignTransform}, ${
				triggerHeight + totalPointerSpace
			}px)`,
			right: `translate(${
				triggerWidth + totalPointerSpace
			}px, ${verticalAlignTransform})`,
			left: `translate(-${totalWidth}px, ${verticalAlignTransform})`,
		}[placement];

		const onLight = {
			backgroundColor: COLOR_BG_DARK_VARIANT,
			color: COLOR_FG_LIGHT_3,
			svg: {
				fill: COLOR_BG_DARK_VARIANT,
			},
		};

		const onDark = {
			backgroundColor: COLOR_BG_WHITE,
			color: COLOR_FG_DARK_1,
			svg: {
				fill: COLOR_BG_WHITE,
			},
		};

		const styles = {
			bg_dark: onDark,
			bg_dark_variant: onDark,
			bg_light: onLight,
			bg_light_variant: onLight,
			bg_white: onLight,
		}[colorOn];

		return {
			top,
			left,
			transform,
			padding: theme.space.md,
			borderRadius: theme.radii.sm,
			...styles,
		};
	}};
`;

export const StyledTooltipPointer = styled(Pointer)<StyledTooltipPointerProps>`
	position: absolute;
	bottom: 100%;
	height: ${TOOLTIP_POINTER_HEIGHT}px;
	width: ${TOOLTIP_POINTER_WIDTH}px;

	${({ placement, alignment, triggerCoordinates }) => {
		const { width: triggerWidth, height: triggerHeight } = triggerCoordinates;

		const TOOLTIP_POINTER_HORIZONTAL_OFFSET =
			triggerWidth / 2 > TOOLTIP_POINTER_WIDTH
				? `${triggerWidth / 2}px`
				: `${TOOLTIP_POINTER_WIDTH}px`;
		const TOOLTIP_TRIGGER_VERTICAL_OFFSET =
			triggerHeight / 2 > TOOLTIP_POINTER_WIDTH
				? `${triggerHeight / 2}px`
				: `${TOOLTIP_POINTER_WIDTH}px`;

		const horizontalAlignmentCoordinates = {
			start: TOOLTIP_POINTER_HORIZONTAL_OFFSET,
			center: "50%",
			end: `calc(100% - ${TOOLTIP_POINTER_HORIZONTAL_OFFSET})`,
		}[alignment];

		const verticalAlignmentCoordinates = {
			start: TOOLTIP_TRIGGER_VERTICAL_OFFSET,
			center: "50%",
			end: `calc(100% - ${TOOLTIP_TRIGGER_VERTICAL_OFFSET})`,
		}[alignment];

		return {
			top: {
				left: horizontalAlignmentCoordinates,
				top: "100%",
				transform: "translateX(-50%) rotate(180deg)",
			},
			bottom: {
				left: horizontalAlignmentCoordinates,
				bottom: "100%",
				transform: "translateX(-50%)",
			},
			left: {
				left: `calc(100% - ${TOOLTIP_POINTER_HEIGHT}px)`,
				top: verticalAlignmentCoordinates,
				transform: "translateY(-50%) rotate(90deg)",
			},
			right: {
				right: `calc(100% - ${TOOLTIP_POINTER_HEIGHT + 0.2}px)`, //the 0.2 is temporary solution for retina displays
				top: verticalAlignmentCoordinates,
				transform: "translateY(-50%) rotate(-90deg)",
			},
		}[placement];
	}}
`;

export default StyledTooltip;
